import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useMotionValue, useReducedMotion } from "motion/react";

type CardDrag = {
  id: string; pointerId: number; startX: number; startY: number; offsetX: number; offsetY: number;
  width: number; height: number; listRect: DOMRect; slots: DOMRect[]; original: string[]; order: string[]; active: boolean;
};
type Props<T extends { id: string }> = {
  items: T[]; onMove: (id: string, targetId: string) => void; onRemove: (id: string) => void;
  itemLabel: (item: T, index: number) => string;
  renderContent: (item: T, index: number, onRemove?: () => void) => ReactNode;
  listLabel?: string; listClassName?: string; itemClassName?: string; children?: ReactNode;
};

/** 直达和中转共用整卡预览、浮层与键盘排序；有效松手才提交主备顺序。 */
export function RailwaySortableSelections<T extends { id: string }>({ items, onMove, onRemove, itemLabel, renderContent,
  listLabel, listClassName = "", itemClassName = "", children }: Props<T>) {
  const latest = useRef({ items, onMove }); latest.current = { items, onMove };
  const list = useRef<HTMLOListElement>(null);
  const drag = useRef<CardDrag | null>(null);
  const [previewOrder, setPreviewOrder] = useState<string[] | null>(null);
  const [floating, setFloating] = useState<{ id: string; width: number; height: number } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const x = useMotionValue(0); const y = useMotionValue(0);
  const reducedMotion = useReducedMotion();
  const ids = items.map((item) => item.id);
  const identity = JSON.stringify(ids);
  const finish = useCallback((commit: boolean) => {
    const current = drag.current; drag.current = null;
    setPreviewOrder(null); setFloating(null);
    if (!current?.active || !commit || JSON.stringify(current.original) !== JSON.stringify(latest.current.items.map((item) => item.id))) return;
    const targetIndex = current.order.indexOf(current.id);
    const targetId = current.original[targetIndex];
    if (targetId && targetId !== current.id) {
      latest.current.onMove(current.id, targetId);
      setAnnouncement(`已调整到第 ${targetIndex + 1} 项，${targetIndex === 0 ? "主选" : "备选"}`);
    }
  }, []);
  useEffect(() => { if (drag.current && JSON.stringify(drag.current.original) !== identity) finish(false); }, [identity, finish]);
  useEffect(() => {
    const move = (event: PointerEvent) => {
      const current = drag.current;
      if (!current || event.pointerId !== current.pointerId || !list.current) return;
      if (!current.active && Math.hypot(event.clientX - current.startX, event.clientY - current.startY) < 6) return;
      if (!current.active) { current.active = true; setFloating({ id: current.id, width: current.width, height: current.height }); }
      event.preventDefault();
      x.set(event.clientX - current.offsetX); y.set(event.clientY - current.offsetY);
      const bounds = list.current.getBoundingClientRect();
      const pointX = event.clientX - bounds.left + current.listRect.left;
      const pointY = event.clientY - bounds.top + current.listRect.top;
      const targetIndex = current.slots.findIndex((rect) => pointX >= rect.left && pointX <= rect.right && pointY >= rect.top && pointY <= rect.bottom);
      const fromIndex = current.order.indexOf(current.id);
      if (targetIndex < 0 || targetIndex === fromIndex) return;
      const next = [...current.order]; next.splice(targetIndex, 0, ...next.splice(fromIndex, 1));
      current.order = next; setPreviewOrder(next);
    };
    const up = (event: PointerEvent) => {
      if (drag.current?.pointerId !== event.pointerId) return;
      const bounds = list.current?.getBoundingClientRect();
      finish(!!bounds && event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom);
    };
    const cancel = () => finish(false);
    const key = (event: KeyboardEvent) => { if (event.key === "Escape" && drag.current) { event.preventDefault(); event.stopPropagation(); finish(false); } };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up); window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", key, true); window.addEventListener("blur", cancel);
    return () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel); window.removeEventListener("keydown", key, true);
      window.removeEventListener("blur", cancel); drag.current = null;
    };
  }, [finish, x, y]);
  const begin = (event: ReactPointerEvent<HTMLLIElement>, item: T) => {
    if (event.button !== 0 || items.length < 2 || (event.target as Element).closest("button") || !list.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    drag.current = { id: item.id, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY,
      offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top, width: rect.width, height: rect.height,
      listRect: list.current.getBoundingClientRect(), slots: Array.from(list.current.children, (node) => node.getBoundingClientRect()), original: ids, order: ids, active: false };
    x.set(rect.left); y.set(rect.top);
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const ordered = previewOrder ? previewOrder.flatMap((id) => items.filter((item) => item.id === id)) : items;
  const floatingItem = floating && items.find((item) => item.id === floating.id);
  return <>
    {items.length > 0 && <ol ref={list} className={`rail-selected-list${listClassName ? ` ${listClassName}` : ""}`} aria-label={listLabel}>
      {ordered.map((item, index) => <motion.li layout="position" key={item.id}
        transition={reducedMotion ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 32 }}
        className={`rail-selected-item ${itemClassName} ${index === 0 ? "rail-selected-primary" : ""} ${floating?.id === item.id ? "rail-selected-dragging" : ""}`}
        tabIndex={0} aria-label={itemLabel(item, index)}
        aria-roledescription="可排序方案卡片" title="拖动整张卡片调整顺序；也可聚焦后按方向键移动"
        onPointerDown={(event) => begin(event, item)} onDragStart={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget || !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) return;
          event.preventDefault();
          const targetIndex = index + (["ArrowUp", "ArrowLeft"].includes(event.key) ? -1 : 1);
          const target = items[targetIndex];
          if (target) { onMove(item.id, target.id); setAnnouncement(`已调整到第 ${targetIndex + 1} 项，${targetIndex === 0 ? "主选" : "备选"}`); }
        }}>
        {renderContent(item, index, () => onRemove(item.id))}
      </motion.li>)}
    </ol>}
    {children}
    {createPortal(<AnimatePresence>{floating && floatingItem && <motion.div key={floating.id} aria-hidden="true"
      className={`rail-selected-item ${itemClassName} rail-selected-drag-overlay ${ordered[0]?.id === floating.id ? "rail-selected-primary" : ""}`}
      style={{ x, y, width: floating.width, height: floating.height }}
      initial={{ scale: 1, rotate: 0 }} animate={{ scale: reducedMotion ? 1 : 1.035, rotate: reducedMotion ? 0 : 1 }}
      exit={{ scale: 1, rotate: 0, opacity: 0 }} transition={{ duration: reducedMotion ? 0 : 0.16 }}>
      {renderContent(floatingItem, ordered.findIndex((item) => item.id === floating.id))}
    </motion.div>}</AnimatePresence>, document.body)}
    <span className="rail-sr-only" role="status">{announcement}</span>
  </>;
}
